import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, Mail, Save, Send, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole } from '../../lib/roles';
import { toast } from 'sonner';
import { Tooltip } from '../../components/ui/tooltip';

const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50';

interface BrandingForm {
  email_display_name: string;
  email_reply_to: string;
}

const DEFAULTS: BrandingForm = {
  email_display_name: '',
  email_reply_to: '',
};

/**
 * Outcome of the last Send Test Email click. Both branches are rendered
 * inline on the page rather than as a toast: the success names the inbox the
 * operator now has to go and check, and the failure is the API's own message
 * (no Reply-To saved, hourly limit reached), which must stay readable.
 */
type TestSendResult =
  | { kind: 'sent'; to: string; resendId: string | null }
  | { kind: 'failed'; message: string };

/**
 * Every route on the settings router wraps its payload as { success, data }.
 * Distinguish "envelope with absent data" from "data" explicitly — a `||`
 * fallback would read the envelope itself as the payload.
 */
function unwrap<T = any>(body: any): T | null | undefined {
  if (body && typeof body === 'object' && 'data' in body) return body.data;
  return body;
}

function isValidEmail(email: string): boolean {
  if (!email) return true; // blank is allowed
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function sanitizeDisplayName(val: string): string {
  return val.replace(/[<>"]/g, '').replace(/\n/g, '');
}

export default function EmailBrandingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, company } = useAuth();
  const isAdmin = isAdminRole(user?.role) || user?.role === 'super_admin';

  const [form, setForm] = useState<BrandingForm>(DEFAULTS);
  const [initialized, setInitialized] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [testResult, setTestResult] = useState<TestSendResult | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'email-branding'],
    queryFn: async () => {
      const res = await api.emailBranding.get();
      return unwrap(res.data) ?? {};
    },
  });

  useEffect(() => {
    if (data && !initialized) {
      setForm({
        email_display_name: data.email_display_name || '',
        email_reply_to: data.email_reply_to || '',
      });
      setInitialized(true);
      setDirty(false);
    }
  }, [data, initialized]);

  const update = (key: keyof BrandingForm, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const saveMutation = useMutation({
    mutationFn: (payload: BrandingForm) => api.emailBranding.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'email-branding'] });
      setDirty(false);
      // A result from before the save describes a different saved address.
      setTestResult(null);
      toast.success('Email branding updated');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to save'),
  });

  // The endpoint sends to the SAVED Reply-To, so the button reads the saved
  // value, never the form: a dirty form would test an address the customer
  // never sees. `sent`/`resendId` are returned too; only `to` is shown.
  const savedReplyTo: string = (data?.email_reply_to || '').trim();

  const testMutation = useMutation({
    mutationFn: () => api.emailBranding.sendTest(),
    onSuccess: (res) => {
      const d = unwrap(res.data);
      const to = typeof d?.to === 'string' && d.to ? d.to : savedReplyTo;
      const resendId = d?.resendId ?? d?.resend_id ?? null;
      setTestResult({ kind: 'sent', to, resendId: resendId ? String(resendId) : null });
    },
    onError: (err: any) => {
      // 400 (nothing saved) and 429 (hourly limit) both carry the reason in
      // `error`. Render it verbatim; a generic message would hide the limit.
      const message =
        err?.response?.data?.error
        || err?.response?.data?.message
        || err?.message
        || 'The test email could not be sent';
      setTestResult({ kind: 'failed', message: String(message) });
    },
  });

  const handleSave = () => {
    if (form.email_reply_to && !isValidEmail(form.email_reply_to)) {
      toast.error('Please enter a valid reply-to email address');
      return;
    }
    saveMutation.mutate(form);
  };

  const handleSendTest = () => {
    setTestResult(null);
    testMutation.mutate();
  };

  // Dirty form guard
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) { e.preventDefault(); }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  if (!isAdmin) {
    return (
      <div className="min-h-full flex flex-col items-center justify-center py-20 px-6">
        <Mail size={40} className="text-fieldiq-muted mb-4" />
        <h2 className="text-lg font-bold text-white mb-2">Admin Access Required</h2>
        <p className="text-sm text-fieldiq-text text-center mb-4">
          You need admin permissions to manage email branding.
        </p>
        <button
          onClick={() => navigate('/settings')}
          className="text-sm text-fieldiq-accent hover:underline"
        >
          Back to Settings
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
      </div>
    );
  }

  // Preview values
  const displayName = form.email_display_name || company?.name || 'Your Company';
  const fromSample = data?.preview?.from_sample || `${displayName} <tickets@field-iq.ai>`;
  const replyToSample = form.email_reply_to || null;

  const replyToInvalid = !!form.email_reply_to && !isValidEmail(form.email_reply_to);
  const testDisabledReason = dirty
    ? 'Save your changes first.'
    : !savedReplyTo
      ? 'Save a Reply-To address first.'
      : null;
  const testDisabled = !!testDisabledReason || testMutation.isPending;

  return (
    <div className="min-h-full">
      {/* The Organization workspace renders the title and the tab strip; the
          page keeps its actions, as a right-aligned row. */}
      <div className="px-6 pt-4">
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-3">
            {/* Only admins reach this page (gate above), and the endpoint is
                strict-admin, so the button never shows to a role it would 403. */}
            <Tooltip content={testDisabledReason ?? `Send a test email to ${savedReplyTo}`}>
              <button
                type="button"
                onClick={handleSendTest}
                disabled={testDisabled}
                aria-label="Send Test Email"
                className="flex items-center gap-2 bg-fieldiq-card border border-fieldiq-border text-white px-4 py-2.5 rounded-lg font-semibold text-sm hover:border-fieldiq-accent/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {testMutation.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                {testMutation.isPending ? 'Sending...' : 'Send Test Email'}
              </button>
            </Tooltip>
            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty || saveMutation.isPending || replyToInvalid}
              className="flex items-center gap-2 bg-fieldiq-accent text-fieldiq-dark px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-amber-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saveMutation.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Save size={14} />
              )}
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 space-y-4">
        {testResult?.kind === 'sent' && (
          <div
            role="status"
            className="flex items-start gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300"
          >
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
            <span>
              Test sent to <span className="font-semibold text-white">{testResult.to}</span> — check that inbox, and reply to it to confirm replies come back.
              {testResult.resendId && (
                <span className="block text-xs text-emerald-300/70 mt-1">Provider message id: {testResult.resendId}</span>
              )}
            </span>
          </div>
        )}
        {testResult?.kind === 'failed' && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
          >
            <XCircle size={16} className="mt-0.5 shrink-0" />
            <span>{testResult.message}</span>
          </div>
        )}

        <p className="text-sm text-fieldiq-muted">
          Customize how emails from FieldIQ appear to your customers. Replies will be routed to the address you set below.
        </p>

        {/* Display Name */}
        <div>
          <label className="text-sm font-medium text-white block mb-1">Display Name</label>
          <p className="text-xs text-fieldiq-muted mb-2">
            Shown to recipients as the sender name. Leave blank to use your company name.
          </p>
          <input
            className={inputCls}
            value={form.email_display_name}
            onChange={e => update('email_display_name', sanitizeDisplayName(e.target.value))}
            placeholder={company?.name || 'Your Company Name'}
            maxLength={255}
          />
        </div>

        {/* Reply-To Email */}
        <div>
          <label className="text-sm font-medium text-white block mb-1">Reply-To Email Address</label>
          <p className="text-xs text-fieldiq-muted mb-2">
            When customers reply to FieldIQ emails, replies are sent here. Leave blank to disable replies.
            Save, then use Send Test Email to confirm the saved address receives mail.
          </p>
          <input
            type="email"
            className={`${inputCls} ${replyToInvalid ? 'border-red-500/50' : ''}`}
            value={form.email_reply_to}
            onChange={e => update('email_reply_to', e.target.value)}
            placeholder="admin@yourcompany.com"
          />
          {replyToInvalid && (
            <p className="text-xs text-red-400 mt-1">Please enter a valid email address</p>
          )}
        </div>

        {/* Live Preview */}
        <div>
          <h3 className="text-sm font-semibold text-white mb-3">Email Preview</h3>
          <div className="bg-fieldiq-dark border border-fieldiq-border rounded-xl p-4 font-mono text-xs space-y-1">
            <div className="flex">
              <span className="text-fieldiq-muted w-20 shrink-0">From:</span>
              <span className="text-white">{fromSample}</span>
            </div>
            <div className="flex">
              <span className="text-fieldiq-muted w-20 shrink-0">Reply-To:</span>
              {replyToSample ? (
                <span className="text-white">{replyToSample}</span>
              ) : (
                <span className="text-fieldiq-muted italic">(none — customers cannot reply to this email)</span>
              )}
            </div>
            <div className="flex">
              <span className="text-fieldiq-muted w-20 shrink-0">Subject:</span>
              <span className="text-white">Field Ticket FT-00010 ready for approval</span>
            </div>
            <div className="border-t border-fieldiq-border my-3" />
            <p className="text-fieldiq-text">Hi John,</p>
            <p className="text-fieldiq-text mt-2">Your field ticket is ready for review and approval.</p>
            <p className="text-fieldiq-text">Please click below to view and sign...</p>
          </div>
        </div>
      </div>
    </div>
  );
}
